import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { TranslateService } from "@ngx-translate/core";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatFacades from '../../internal';

@Injectable()
export class PageEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static attachDetachedPage(store: Store,
                            id: string,
                            parentNode: FlatModels.NodeEntityModels.NodeEntityType,
                            clusterParentUpdate = false) {
    // Get Data
    const typedNodes = store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    const pages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    
    // Get Page
    const page = pages[id];
    
    // Get Node
    let node = structuredClone(Object.values(typedNodes).find(n => n.page === page.id) as FlatModels.NodeEntityModels.SlideNode);
    const {route} = FlatFacades.NodeEntityFacade.getPageCopyCountAndRoute(node?.route || page.title, parentNode?.id, node?.id, store);
    if (node) {
      node.id = uuid();
      node.parent = parentNode.id;
      node.parentId = parentNode.id;
      node.route = route;
      node.position = parentNode.children.length;
    } else {
      node = {
        id: uuid(),
        shopId: null,
        parent: parentNode.id,
        parentId: parentNode.id,
        route: route,
        position: parentNode.children.length,
        page: page.id,
        children: [],
        hidden: false,
        restricted: false,
        shares: [],
        settings: {},
      };
    }
    
    // Update Node
    parentNode.children.push(node.id);
    
    // Copy Node
    return of(node.id).pipe(
      mergeMap(() => {
        return store.dispatch(new FlatActions.NodeEntityActions.AddEntity(node))
      }),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(parentNode));
      }),
      mergeMap(() => of(node.id))
    );
  }
  
  static attachDetachedPages(store: Store,
                             ids: string[],
                             parentNode: FlatModels.NodeEntityModels.NodeEntityType,
                             clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Clone Parent Node
    const cloneParentNode = structuredClone(parentNode);
    
    // Copy Nodes
    return forkJoin(ids.map(id => PageEntityFacade.attachDetachedPage(store, id, cloneParentNode, true))).pipe(
      mergeMap((selectIds) => {
        if (clusterParentUpdate) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(cloneParentNode)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
    )
  }

  static copyPage(store: Store,
                  mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                  id: string,
                  node: FlatModels.NodeEntityModels.NodeEntityType,
                  asTraining: boolean | null,
                  withAnnotations: boolean,
                  clusterParentUpdate = false) {
    // Check If Page Mapping Exists
    if (mappings.page[id]) {
      return of({});
    }
    
    // Get Data
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    
    // Get Page
    const page = structuredClone(typedPages[id]);

    // Update Page Mappings
    mappings.page[id] = uuid();
    
    // Update Page
    page.id = mappings.page[id];
    if (asTraining !== undefined) {
      if (asTraining) {
        page.type = FlatModels.PageEntityModels.PageType.enum.Training;
      } else {
        page.type = FlatModels.PageEntityModels.PageType.enum.Slide;
      }
    }
    
    // Update Node
    node.page = page.id;
    
    // Copy Page
    return of({}).pipe(
      mergeMap((data) => {
        let slide: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
        let annotations: string[];
        let layers: string[];
        let questions: string[];
        let groups: string[];
        let pois: string[];
        switch (page.type) {
          case FlatModels.PageEntityModels.PageType.enum.Slide:
          case FlatModels.PageEntityModels.PageType.enum.Training:
            slide = page as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
            slide.slideId = null;
            slide.pdfMedia = null;
            slide.pdfMediaAuthor = null;
            
            // TODO replace links in text here
            
            // Annotations
            annotations = structuredClone(slide.annotations);
            slide.annotations = [];
            
            // Layers
            layers = structuredClone(slide.layers);
            slide.layers = [];
            
            // Questions
            questions = structuredClone(slide.questions);
            slide.questions = [];
            
            // Groups
            groups = structuredClone(slide.groups);
            slide.groups = [];
            
            // Pois
            pois = structuredClone(slide.pois);
            slide.pois = [];
            
            return FlatFacades.LayerEntityFacade.copyLayers(store, mappings, layers, slide, false, withAnnotations, true).pipe(
              mergeMap((data) => {
                if (withAnnotations) {
                  return FlatFacades.AnnotationEntityFacade.copyAnnotations(store, mappings, annotations, slide, null, undefined, true);
                }
                return of(data);
              }),
              mergeMap(() =>
                FlatFacades.QuestionEntityFacade.copyQuestions(store, questions, slide, true)
              ),
              mergeMap(() =>
                FlatFacades.GroupEntityFacade.copyGroups(store, mappings, groups, slide, withAnnotations, true)
              ),
              mergeMap(() =>
                FlatFacades.PoiEntityFacade.copyPois(store, mappings, pois, slide, null, withAnnotations, true)
              ),
            )
          default:
            throw new FlatModels.ErrorControlModels.MedsurfError(
              'notification.type_not_supported',
              FlatModels.ErrorControlModels.ErrorLevel.enum.error,
              FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
              "LIBS.FlatFacades.PageEntityFacade.copyPage"
            );
        }
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.PageEntityActions.AddEntity(page))
      ),
      mergeMap(() =>
        store.dispatch(new FlatActions.SlideControlActions.AddAvailableSlide(page.id))
      ),
      mergeMap(() =>
        store.dispatch(new FlatActions.SlideControlActions.GetSlideEntitySuccess({entity: page} as any))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(node));
      }),
    )
  }

  static removePage(store: Store,
                    id: string) {
    // Get Data
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const typedGroups = store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$);
    const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    const typedQuestions = store.selectSnapshot(FlatStates.QuestionEntityState.typedEntities$);
    const typedAnnotations = store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    const typedVocabularyGroups = store.selectSnapshot(FlatStates.VocabularyGroupEntityState.typedEntities$);
    
    // Get Page
    const page = structuredClone(typedPages[id]);
    
    // Check Type
    switch(page.type) {
      case FlatModels.PageEntityModels.PageType.enum.Folder:
      case FlatModels.PageEntityModels.PageType.enum.Grid:
      case FlatModels.PageEntityModels.PageType.enum.Case:
      case FlatModels.PageEntityModels.PageType.enum.QuizStart:
      case FlatModels.PageEntityModels.PageType.enum.QuizRun:
        // Remove Page
        return of({}).pipe(
          mergeMap(() => 
            FlatFacades.NodeEntityFacade.removeNodesByPageId(store, id)
          ),
          mergeMap(() =>
            store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveNode(id))
          ),
          mergeMap(() =>
            store.dispatch(new FlatActions.PageEntityActions.RemoveEntity(page))
          ),
          mergeMap(() =>
            store.dispatch(new FlatActions.SlideControlActions.RemoveAvailableSlide(page.id))
          ),
        );
      case FlatModels.PageEntityModels.PageType.enum.Slide:
      case FlatModels.PageEntityModels.PageType.enum.Training: {
        const slide = page as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
        // Get Groups
        const groups = (slide.groups || []).reduce((arr, group) => {
          const entity = structuredClone(typedGroups[group]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.GroupEntityModels.Group[]);
        
        // Get Questions
        const questions = (slide.questions || []).reduce((arr, question) => {
          const entity = structuredClone(typedQuestions[question]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.QuestionEntityModels.QuestionEntityType[]);
        
        // Get Pois
        const pois = (slide.pois || []).reduce((arr, poi) => {
          const entity = structuredClone(typedPois[poi]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.PointOfInterestEntityModels.PointOfInterest[]);
        
        // Get Annotations
        const annotations = (slide.annotations || []).reduce((arr, annotation) => {
          const entity = structuredClone(typedAnnotations[annotation]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.AnnotationEntityModels.AnnotationEntityType[]);
        
        // Get Layers
        const layers = (slide.layers || []).reduce((arr, layer) => {
          const entity = structuredClone(typedLayers[layer]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.LayerEntityModels.Layer[]);
        
        // Remove Page
        return of({}).pipe(
          mergeMap(() =>
            FlatFacades.NodeEntityFacade.removeNodesByPageId(store, id)
          ),
          mergeMap(() =>
            store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveNode(id))
          ),
          mergeMap((result) => {
            if (groups.length > 0) {
              return forkJoin(groups.map(group => store.dispatch(new FlatActions.AnnotationGroupEntityActions.RemoveEntity(group))));
            }
            return of(result);
          }),
          mergeMap((result) => {
            if (questions.length > 0) {
              return forkJoin(questions.map(question => FlatFacades.QuestionEntityFacade.removeQuestion(store, question.id, true)));
            }
            return of(result);
          }),
          mergeMap((result) => {
            if (pois.length > 0) {
              return forkJoin(pois.map(poi => FlatFacades.PoiEntityFacade.removePoi(store, poi.id, true)));
            }
            return of(result);
          }),
          mergeMap((result) => {
            if (annotations.length > 0) {
              return forkJoin(annotations.map(annotation => FlatFacades.AnnotationEntityFacade.removeAnnotation(store, annotation.id, true)));
            }
            return of(result);
          }),
          mergeMap((result) => {
            if (layers.length > 0) {
              return forkJoin(layers.map(layer => FlatFacades.LayerEntityFacade.removeLayer(store, layer.id, true)));
            }
            return of(result);
          }),
          mergeMap(() =>
            store.dispatch(new FlatActions.PageEntityActions.RemoveEntity(page))
          ),
          mergeMap(() =>
            store.dispatch(new FlatActions.SlideControlActions.RemoveAvailableSlide(page.id))
          ),
        );
      }
      case FlatModels.PageEntityModels.PageType.enum.Vocabulary: {
        const vocabulary = page as FlatModels.PageEntityModels.Vocabulary;
        // Get Vocabulary Groups
        const vocabularyGroups = vocabulary.vocabularyGroups.reduce((arr, vocabularyGroup) => {
          const entity = structuredClone(typedVocabularyGroups[vocabularyGroup]);
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.VocabularyGroupEntityModels.VocabularyGroup[]);
        // Remove Page
        return of({}).pipe(
          mergeMap(() =>
            FlatFacades.NodeEntityFacade.removeNodesByPageId(store, id)
          ),
          mergeMap(() =>
            store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveNode(id))
          ),
          mergeMap((data) => {
            if (vocabularyGroups.length > 0) {
              return forkJoin(vocabularyGroups.map(vocabularyGroup => store.dispatch(new FlatActions.VocabularyGroupEntityActions.RemoveEntity(vocabularyGroup))));
            }
            return of(data);
          }),
          mergeMap(() =>
            store.dispatch(new FlatActions.PageEntityActions.RemoveEntity(page))
          ),
          mergeMap(() =>
            store.dispatch(new FlatActions.SlideControlActions.RemoveAvailableSlide(page.id))
          ),
        );
      }
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.type_not_supported',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.PageEntityFacade.removePage"
        );
    }
  }
  
  static removePages(store: Store,
                     ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Remove Pages
    return forkJoin(ids.map(id => PageEntityFacade.removePage(store, id))).pipe(
      /*
      mergeMap((data: { data: Store, parentNode: FlatModels.NodeEntityModels.NodeEntityType }[]) => {
        const nodeMap = data.reduce((arr, arrangeData) => {
          const parentNode = structuredClone(arr.get(arrangeData.parentNode.id));
          if (parentNode) {
            parentNode.children = arrangeData.parentNode.children.filter((id: string) => parentNode.children.includes(id));
            arr.set(arrangeData.parentNode.id, parentNode);
          } else {
            arr.set(arrangeData.parentNode.id, arrangeData.parentNode);
          }
          return arr;
        }, new Map<string, FlatModels.NodeEntityModels.NodeEntityType>())
        return forkJoin(Array.from(nodeMap.values()).map((node) => store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(node, node.children))));
      }),
      */
    );
  }
  
  static removeAnnotationFromPage(store: Store,
                                  id: string,
                                  pageId: string,
                                  clusterParentUpdate = false) {
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const page = structuredClone(typedPages[pageId]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    
    // Check Index
    const index = page.annotations.findIndex((a: string) => a === id);
    if (index < 0) {
      return of({page});
    }
    
    // Update Page
    page.annotations.splice(index, 1);
    
    // Remove Annotation From Page
    return of({page}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }
  
  static removeAnnotationsFromPage(store: Store,
                                   ids: string[],
                                   pageId: string,
                                   clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of({pageId})
    }
    
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const page = structuredClone(typedPages[pageId]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    
    for (const id of ids) {
      // Check Index
      const index = page.annotations.findIndex((a: string) => a === id);
      if (index > -1) {
        // Update Page
        page.annotations.splice(index, 1);
      }
    }
    
    // Remove Annotation From Page
    return of({page}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }
  
  static removePoiFromPage(store: Store,
                           id: string,
                           pageId: string,
                           clusterParentUpdate = false) {
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const page = structuredClone(typedPages[pageId]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    
    // Check Index
    const index = page.pois.findIndex((a: string) => a === id);
    if (index < 0) {
      return of({page});
    }
    
    // Update Page
    page.pois.splice(index, 1);
    
    // Remove Poi From Page
    return of({page}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }
  
  static removePoisFromPage(store: Store,
                            ids: string[],
                            pageId: string,
                            clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of({pageId})
    }
    
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const page = structuredClone(typedPages[pageId]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    
    for (const id of ids) {
      // Check Index
      const index = page.pois.findIndex((a: string) => a === id);
      if (index > -1) {
        // Update Page
        page.pois.splice(index, 1);
      }
    }
    
    // Remove Pois From Page
    return of({page}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }
  
  static removePoisAndAnnotationsFromPage(store: Store,
                                          poiIds: string[],
                                          annotationIds: string[],
                                          pageId: string,
                                          clusterParentUpdate = false) {
    if (poiIds.length === 0 && annotationIds.length === 0) {
      return of({pageId})
    }
    
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const page = structuredClone(typedPages[pageId]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    
    // Pois
    for (const id of poiIds) {
      // Check Index
      const index = page.pois.findIndex((a: string) => a === id);
      if (index > -1) {
        // Update Page
        page.pois.splice(index, 1);
      }
    }
    
    // Annotations
    for (const id of annotationIds) {
      // Check Index
      const index = page.annotations.findIndex((a: string) => a === id);
      if (index > -1) {
        // Update Page
        page.annotations.splice(index, 1);
      }
    }
    
    // Remove Pois And Annotations From Page
    return of({page}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
    );
  }

  //</editor-fold>
}
